import {
    Dialog,
    DialogContent,
    Button,
    IconButton,
    Typography,
    AppBar,
    Stack,
    Box,
    Autocomplete,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormikContext } from "formik";
import { CustomTitle, Loader } from "../HelperFunctions/HelperFunctions";
import { useEffect, useMemo, useState } from "react";
import { TimePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { enqueueSnackbar } from "notistack";

const MAIL_FREQUENCIES = ["daily", "weekly", "monthly"];

const WEEKLY_DAYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
];

const MONTHLY_DAYS = [
    "start_of_the_month",
    "mid_of_the_month",
    "end_of_the_month",
];

const TIME_ZONES = ["EST", "CST", "MST", "PST", "IST"];

/** Display labels that match the design screenshots. */
const DAY_LABELS = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    start_of_the_month: "Start of the Month",
    mid_of_the_month: "Mid of the Month",
    end_of_the_month: "End of the Month",
};

function dayOptionsForFrequency(mailFrequency) {
    if (mailFrequency === "weekly") return WEEKLY_DAYS;
    if (mailFrequency === "monthly") return MONTHLY_DAYS;
    return [];
}

function formatDayLabel(value) {
    if (!value) return "";
    return DAY_LABELS[value] || CustomTitle(value);
}

const FrequencyDialog = ({ open, onClose }) => {
    const { values, handleChange, handleSubmit, setFieldValue } =
        useFormikContext();
    const [loading, setLoading] = useState(false);

    const mailFrequency = values.frequency?.mail_frequency || "daily";
    const dayOptions = useMemo(
        () => dayOptionsForFrequency(mailFrequency),
        [mailFrequency],
    );

    // When frequency changes, clear / default the Day field appropriately.
    useEffect(() => {
        if (!open) return;
        if (mailFrequency === "daily") {
            if (values.frequency?.day) {
                setFieldValue("frequency.day", "");
            }
            return;
        }
        const allowed = dayOptionsForFrequency(mailFrequency);
        const current = values.frequency?.day || "";
        if (!allowed.includes(current)) {
            setFieldValue("frequency.day", allowed[0] || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mailFrequency]);

    const frequencyOptions = [
        {
            heading: "Mailing Frequency",
            value: mailFrequency,
            name: "frequency.mail_frequency",
            label: MAIL_FREQUENCIES,
        },
        {
            heading: "Day",
            value: values.frequency?.day || null,
            name: "frequency.day",
            label: dayOptions,
            hide: mailFrequency === "daily",
        },
        {
            heading: "Time",
            isTime: true,
        },
        {
            heading: "Time Zone",
            value: values.frequency?.time_zone || null,
            name: "frequency.time_zone",
            label: TIME_ZONES,
        },
    ];

    const handleSaveAndNext = async () => {
        const freq = values.frequency || {};
        const selectedFreq = freq.mail_frequency || "daily";

        if (selectedFreq === "daily") {
            setFieldValue("frequency.day", "");
        } else if (!freq.day) {
            enqueueSnackbar("Please select a day for the schedule", {
                variant: "warning",
            });
            return;
        }

        if (!freq.time_zone || !freq.time) {
            enqueueSnackbar(
                "Time and time zone must be selected to create the schedule",
                { variant: "warning" },
            );
            setLoading(false);
            return;
        }
        onClose();
        handleSubmit();
    };

    const handleTimeChange = (time) => {
        const formattedTime = time
            ? time.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
              })
            : null;
        handleChange({
            target: {
                name: "frequency.time",
                value: formattedTime,
            },
        });
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return null;
        const [hours, minutes] = timeStr.split(":");
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(0);
        return date;
    };

    const handleFrequencyChange = (name, newValue) => {
        handleChange({
            target: { name, value: newValue },
        });
        if (name === "frequency.mail_frequency") {
            if (newValue === "daily") {
                setFieldValue("frequency.day", "");
            } else {
                const allowed = dayOptionsForFrequency(newValue);
                setFieldValue("frequency.day", allowed[0] || "");
            }
        }
    };

    return (
        <Dialog
            open={open}
            sx={{
                "& .MuiDialog-paper": {
                    minWidth: "35%",
                    minHeight: "40%",
                    top: 10,
                    position: "fixed",
                },
            }}
            PaperProps={{
                elevation: 0,
                sx: { border: "1px solid rgba(0, 0, 0, 0.12)" },
            }}
        >
            <AppBar
                sx={{ position: "relative", bgcolor: "#d7d7d7", height: 45 }}
                elevation={0}
            >
                <Box sx={{ height: 45, alignContent: "center", padding: "0 8px" }}>
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <Stack direction={"row"}>
                            <IconButton
                                size="small"
                                edge="start"
                                color="inherit"
                                onClick={onClose}
                                aria-label="close"
                                style={{ backgroundColor: "#686868" }}
                                disableRipple
                            >
                                <CloseIcon sx={{ fontSize: "15px" }} />
                            </IconButton>
                            <Typography
                                sx={{
                                    ml: 2,
                                    flexGrow: 10,
                                    color: "#1f1f1f",
                                }}
                                component="div"
                                fontWeight={600}
                                fontSize={"16px"}
                            >
                                Schedule
                            </Typography>
                        </Stack>
                        <Stack direction={"row"} gap={1}>
                            <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                disableRipple
                                sx={{
                                    textTransform: "none",
                                    backgroundColor: "#b5dcf2",
                                    color: "#000",
                                    fontWeight: 400,
                                }}
                                onClick={handleSaveAndNext}
                            >
                                Save & Finish
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </AppBar>
            <DialogContent>
                <Stack p={2} gap={2} width={"450px"}>
                    {frequencyOptions.map((option, index) => {
                        if (option.hide) return null;

                        return (
                            <Stack
                                key={index}
                                direction="row"
                                gap={15}
                                justifyContent="space-between"
                            >
                                <Typography sx={{ fontSize: "13px" }}>
                                    {option.heading}
                                </Typography>
                                {option.isTime ? (
                                    <TimePicker
                                        format="HH:mm"
                                        size="sm"
                                        style={{ width: 170 }}
                                        menuStyle={{
                                            zIndex: 1300,
                                            overflowY: "auto",
                                        }}
                                        value={formatTime(values.frequency?.time)}
                                        onChange={handleTimeChange}
                                        hideMinutes={(minute) => minute % 10 !== 0}
                                        editable={false}
                                        ranges={[]}
                                    />
                                ) : (
                                    <Autocomplete
                                        size="small"
                                        options={option.label}
                                        value={option.value || null}
                                        disableClearable
                                        onChange={(_, newValue) =>
                                            handleFrequencyChange(
                                                option.name,
                                                newValue,
                                            )
                                        }
                                        getOptionLabel={(label) =>
                                            option.heading === "Day"
                                                ? formatDayLabel(label)
                                                : CustomTitle(label)
                                        }
                                        isOptionEqualToValue={(opt, val) =>
                                            opt === val
                                        }
                                        componentsProps={{
                                            paper: {
                                                sx: { fontSize: "13px" },
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name={option.name}
                                                variant="outlined"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    readOnly: true,
                                                    style: {
                                                        height: "25px",
                                                        fontSize: "11px",
                                                        padding: "0px 8px",
                                                    },
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true,
                                                    style: {
                                                        fontSize: "14px",
                                                        padding: 0,
                                                        height: "25px",
                                                        lineHeight: "25px",
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    sx: { fontSize: "2px" },
                                                }}
                                                style={{ width: "170px" }}
                                            />
                                        )}
                                    />
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
                <Loader loading={loading} />
            </DialogContent>
        </Dialog>
    );
};

export default FrequencyDialog;
